
import { defineComponent, reactive, onBeforeMount, computed } from 'vue'

// @components
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'

// @module components
import CrmBlacklistFilters from '@/modules/CrmModule/components/CrmBlacklistFilters/index.vue'

// @types
import type {
    GetBlacklistParams,
    BlacklistRecord,
    CreateBlacklistRecordPayload,
} from '@/core/types/notifications.types'

// @realisations
import { notifications_realisation } from '@/core/realisations/notifications.realisation'

// @composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const TABLE_COLUMNS = [
    {
        key: 'id',
        name: '№',
    },
    {
        key: 'title',
        name: 'Тип',
    },
    {
        key: 'identifier',
        name: 'Значение',
    },
    {
        key: 'createdAt',
        name: 'Дата добавления',
    },
    {
        key: 'actions',
        name: '',
    },
]

const BLACKLIST_TYPES = [
    { id: 1, name: 'Номер телефона' },
    { id: 2, name: 'Email' },
]

export default defineComponent({
    components: {
        NewTable,
        NewButton,
        Loader,
        NewLimit,
        NewPagination,
        CrmBlacklistFilters,
        Modal,
        NewInput,
        NewSelect,
    },
    setup() {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as BlacklistRecord[],
            pagination: {
                page: 1,
                limit: 50,
                total_pages: 1,
                total_items: 20,
            },
            filters: {
                identifier: null as string | null,
                typeId: null as number | null,
            },
        })

        const create_modal_state = reactive({
            is_opened: false,
            is_loading: false,
            typeId: null as number | null,
            identifier: null as string | null,
        })

        const delete_modal_state = reactive({
            is_opened: false,
            is_loading: false,
            recordId: null as number | null,
        })

        const create_modal_state_form$ = useVuelidate(
            {
                typeId: { required },
                identifier: { required },
            },
            create_modal_state
        )

        const loadRecords = async () => {
            try {
                state.is_loading = true

                const params: GetBlacklistParams = {
                    page: state.pagination.page,
                    limit: state.pagination.limit,
                    identifier: state.filters.identifier,
                    typeId: state.filters.typeId,
                }

                const response = await notifications_realisation.getBlacklist(params)
                state.list = response.data

                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadRecords)

        const onFiltersChanged = (filters: GetBlacklistParams) => {
            state.filters.identifier = filters.identifier as string | null
            state.filters.typeId = filters.typeId as number | null

            loadRecords()
        }

        const onFiltersReset = () => {
            state.filters.identifier = null
            state.filters.typeId = null

            loadRecords()
        }

        const onLimitChange = (new_limit: number) => {
            state.pagination.limit = new_limit
            loadRecords()
        }

        const onChangePage = (new_page: number) => {
            state.pagination.page = new_page
            loadRecords()
        }

        const onCreate = () => {
            create_modal_state.is_opened = true
        }

        const onCreateClose = () => {
            create_modal_state.is_opened = false
            create_modal_state.identifier = null
            create_modal_state.typeId = null
            create_modal_state_form$.value.$reset()
        }

        const onCreateRecord = async () => {
            if (create_modal_state.is_loading) return

            create_modal_state_form$.value.$touch()
            if (create_modal_state_form$.value.$invalid) return

            try {
                create_modal_state.is_loading = true

                const payload: CreateBlacklistRecordPayload = {
                    identifier: create_modal_state.identifier as string,
                    typeId: create_modal_state.typeId as number,
                }

                notifications_realisation.createBlacklistRecord(payload)

                notifications.notify({ type: 'success', title: 'Запись добавлена' })
                loadRecords()
                onCreateClose()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении записи произошла ошибка',
                })
            } finally {
                create_modal_state.is_loading = false
            }
        }

        const onDelete = (recordId: number) => {
            delete_modal_state.recordId = recordId
            delete_modal_state.is_opened = true
        }

        const onDeleteClose = () => {
            delete_modal_state.is_opened = false
            delete_modal_state.recordId = null
        }

        const onDeleteRecord = async () => {
            if (delete_modal_state.is_loading) return

            try {
                delete_modal_state.is_loading = true

                await notifications_realisation.deleteBlacklistRecord(
                    delete_modal_state.recordId as number
                )

                notifications.notify({ type: 'success', title: 'Запись удалена' })
                onDeleteClose()
                loadRecords()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При удалении записи произошла ошибка' })
            } finally {
                delete_modal_state.is_loading = false
            }
        }

        const identifierPlaceholder = computed(() => {
            if (create_modal_state.typeId) {
                if (create_modal_state.typeId === 1) return 'Введите номер телефона'
                if (create_modal_state.typeId === 2) return 'Введите email'
            }

            return 'Введите идентификатор'
        })

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            TABLE_COLUMNS,
            BLACKLIST_TYPES,
            state,
            create_modal_state,
            create_modal_state_form$,
            delete_modal_state,
            havePermissions,
            loadRecords,
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            onCreate,
            onCreateClose,
            onCreateRecord,
            onDelete,
            onDeleteClose,
            onDeleteRecord,
            formatDate,
            identifierPlaceholder,
        }
    },
})
